import type { RatingBoardSummary, RatingPublishedStanding } from '@brush/api-client';

/**
 * §9.5's standings and §7's breakdown, as the portal's rules — the decisions on the ratings screen
 * that produce a perfectly reasonable-looking page when they are wrong.
 *
 * <b>It computes nothing about a rating.</b> Every figure is the server's, read out of the snapshot
 * a recalculation froze; what this decides is whose row is theirs, what a board's freshness reads as,
 * and which board the section opens on.
 *
 * <b>The date formatting is deliberately not `toLocaleDateString`.</b> §2.2's anchor exists to be
 * compared against a schedule, and `03/04/2026` is two different days depending on the reader — the
 * ambiguity this repository refuses everywhere a date is entered, and there is no reason to admit it
 * on the way out. `periodRangeLabel` in `@brush/ui` says the same thing about a period's bounds.
 */

/** Where the reader stands on one board, or null when they are not on it at all. */
export interface OwnPosition {
  rank: number;
  total: number;
  isTied: boolean;
  displayRating: number;
  isQualified: boolean;

  /** §4.6's shortfall, in the server's own words, and null where they qualify. */
  qualificationShortfall: string | null;
}

/**
 * Whether this row offers §7's breakdown — <b>ADR 0011 Decision 10: their own, and nobody else's.</b>
 *
 * The standings publish every player's name, rank and rating to whoever §4.7 admits; a breakdown adds
 * which events somebody played and how each one scored, which is their tournament history assembled
 * and a further step the board itself does not take.
 *
 * <b>This is not the control.</b> The server refuses any other row with a 403 `breakdown_not_yours`,
 * resolving the caller's card from their token — a client that hid nothing would leak nothing. What
 * this buys is not offering a chevron whose answer is a refusal on every row but one.
 *
 * Staff browsing somebody else's portal hold no card here, so nothing offers a breakdown — which is
 * the honest answer rather than a row that refuses when pressed.
 */
export function offersBreakdown(rowPlayerId: string, mePlayerId: string | null): boolean {
  return mePlayerId !== null && rowPlayerId === mePlayerId;
}

/**
 * The reader's own row, found by card and never by position.
 *
 * <b>Absent is a first-class answer.</b> A player who has not scored on a board, or whom §4.3's cohort
 * excludes, is not on it — and the worst available failure here is reporting somebody else's figure
 * under the word "you". So this answers null rather than falling back to anything.
 *
 * <b>The rank is the server's and the total is the whole board.</b> §3.7 has players still level after
 * the full tiebreak chain share a rank with the next one skipping (1, 2, 2, 4), so a rank derived from
 * a row index is wrong before any filter is applied.
 */
export function ownPosition(
  rows: readonly RatingPublishedStanding[],
  mePlayerId: string | null,
): OwnPosition | null {
  if (mePlayerId === null) return null;

  const mine = rows.find((r) => r.playerId === mePlayerId);
  if (mine === undefined) return null;

  return {
    rank: mine.rank,
    total: rows.length,
    isTied: mine.isTied,
    displayRating: mine.displayRating,
    isQualified: mine.isQualified,
    qualificationShortfall: mine.qualificationShortfall,
  };
}

/**
 * How current a board's figures are — §2.2's anchor, and when the engine ran.
 *
 * <b>Both, because they genuinely differ</b> (recomputing an awards-night rating a week later produces
 * a snapshot whose anchor is not the day it was worked out) and because a rating is the one standing
 * that moves on its own: the window slides with the calendar even when no result changes. A figure
 * with no anchor cannot answer "why did my rating drop overnight", and the honest answer is usually
 * that it did not drop — an old result fell out of the last period.
 *
 * <b>A board that has published nothing says so and never names a date.</b> Inventing today's would
 * tell a reader these are current figures about a computation that never ran.
 */
export function boardFreshness(board: RatingBoardSummary | null): string | null {
  if (board === null) return null;

  if (board.asOfDate === null) {
    return 'This rating hasn’t been worked out yet, so there is nothing published to show.';
  }

  const worked = board.computedAt === null ? '' : `, worked out ${when(board.computedAt)}`;

  return (
    `Measured as of ${day(board.asOfDate)}${worked}. ` +
    (board.isFinal
      ? 'These are the final standings.'
      : 'They don’t change until your room works it out again.')
  );
}

/**
 * Which board the section shows.
 *
 * A stored choice wins for as long as it names a board still on the list — a board can be closed, or
 * its visibility narrowed, between one render and the next, and opening the section on an empty panel
 * reads as a fault in the app rather than as a board that went.
 */
export function openingBoardId(
  boards: readonly RatingBoardSummary[],
  chosenId: string | null,
): string | null {
  if (chosenId !== null && boards.some((b) => b.id === chosenId)) return chosenId;
  return boards[0]?.id ?? null;
}

/** `2026-09-30` as `30 Sep 2026`, and unchanged if it is not a date at all. */
function day(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return iso;

  const month = MONTHS[Number(match[2]) - 1];
  if (month === undefined) return iso;

  return `${Number(match[3])} ${month} ${match[1]}`;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * An ISO timestamp as something a player reads, falling back to the raw value rather than to `NaN`.
 *
 * A time rather than a date alone, because this is when a job ran and a reader comparing two visits
 * on one day needs to be able to tell them apart. Locale-formatted, unlike {@link day}: an instant is
 * not a boundary anybody checks arithmetic against, and in the reader's own zone is what they want.
 */
function when(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Which of five things the standings panel is showing — see {@link standingsState}. */
export interface StandingsState {
  kind: 'loading' | 'failed' | 'unpublished' | 'empty' | 'rows';

  /** The failure, already described. Null in every other state, which is the control on it. */
  message: string | null;
}

/**
 * Which of five things the standings panel is showing, and **the precedence between them.**
 *
 * <b>A failure is a state, and it did not used to be one.</b> The screen branched on loading, then
 * on the row count, so ANY failed read fell through to "Your room hasn't worked this rating out
 * yet" — a confident sentence about the room, written by a crash. For the defect that found this it
 * happened to be true, which is worse than being wrong: it is the same sentence for a 403 after a
 * card is revoked, for a 500, and for a dropped connection, and a player acts on it by waiting for
 * standings that are already published.
 *
 * <b>So `failure` is checked before the row count, and that ordering is the whole rule.</b> A failed
 * read has no sequence number and no rows either, so from the data alone it is indistinguishable
 * from a board that has published nothing — which is exactly why the distinction cannot be left to
 * the order of three ternaries in a component.
 *
 * <b>It beats rows as well.</b> Figures left on screen under a failed refresh are the one thing §8
 * refuses everywhere else: standings that look current and are not.
 *
 * <b>Loading beats both</b>, including a failure left over from the previously selected board —
 * otherwise switching board flashes the last one's error, which reads as the new board being broken.
 *
 * <b>`unpublished` and `empty` stay apart.</b> "Your room hasn't worked this rating out yet" and
 * "nobody has a rating on this board" send a player to different expectations: one is worth coming
 * back for and the other means they have not played anything it scores.
 *
 * The failure arrives already described, so this module keeps importing nothing but types — which is
 * what lets `node --test` run it directly.
 */
export function standingsState(input: {
  isLoading: boolean;
  failure: string | null;
  sequenceNumber: number | null;
  rowCount: number;
}): StandingsState {
  if (input.isLoading) return { kind: 'loading', message: null };
  if (input.failure !== null) return { kind: 'failed', message: input.failure };

  if (input.rowCount === 0) {
    return { kind: input.sequenceNumber === null ? 'unpublished' : 'empty', message: null };
  }

  return { kind: 'rows', message: null };
}
